import React, { useEffect, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Grid,
    Box,
    Chip,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import { useInView } from "react-intersection-observer";

function PublicationCard({
    image,
    video,
    title,
    authors,
    projectPage,
    arxivLink,
    conference,
    mediaLink,
    bibtex
}) {
    const boldName = "Filippos Kokkinos";
    const videoRef = useRef(null);
    const { ref: inViewRef, inView } = useInView({ threshold: 0.4 });
    const [copied, setCopied] = useState(false);
    const [bibtexOpen, setBibtexOpen] = useState(false);

    const setRefs = (node) => {
        videoRef.current = node;
        inViewRef(node);
    };

    useEffect(() => {
        if (videoRef.current) {
            if (inView) {
                videoRef.current.play().catch(() => {});
            } else {
                videoRef.current.pause();
            }
        }
    }, [inView]);

    const handleCopyBibtex = () => {
        if (bibtex) {
            navigator.clipboard.writeText(bibtex);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const isTopVenue = conference && (
        conference.includes("ICLR") ||
        conference.includes("NeurIPS") ||
        conference.includes("CVPR") ||
        conference.includes("ECCV") ||
        conference.includes("ICML") ||
        conference.includes("TPAMI") ||
        conference.includes("ICCV")
    );

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    width: "100%",
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    transition: "all 0.25s ease-in-out",
                    background: (theme) =>
                        theme.palette.mode === "dark"
                            ? "rgba(30, 41, 59, 0.7)"
                            : "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(8px)",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                        borderColor: "primary.main"
                    }
                }}
            >
                <Grid container>
                    {/* Media Column */}
                    <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(15, 23, 42, 0.6)"
                                    : "rgba(241, 245, 249, 0.7)",
                            position: "relative",
                            minHeight: { xs: 180, sm: "auto" },
                            p: { xs: 1.5, sm: 2 }
                        }}
                    >
                        {video ? (
                            <CardMedia
                                component="video"
                                src={video}
                                ref={setRefs}
                                preload="none"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    maxHeight: 180,
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                }}
                                title={title}
                                onClick={() => window.open(mediaLink || projectPage || arxivLink, "_blank")}
                                muted
                                loop
                                playsInline
                            />
                        ) : image ? (
                            <CardMedia
                                component="img"
                                src={image}
                                loading="lazy"
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    maxHeight: 180,
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                }}
                                title={title}
                                alt={title}
                                onClick={() => window.open(mediaLink || projectPage || arxivLink, "_blank")}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: 140,
                                    borderRadius: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "linear-gradient(135deg, #1e40af22 0%, #3b82f633 100%)",
                                    color: "primary.main",
                                    fontWeight: 700,
                                    fontSize: "0.9rem"
                                }}
                            >
                                {conference}
                            </Box>
                        )}
                    </Grid>

                    {/* Details Column */}
                    <Grid item xs={12} sm={8}>
                        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                            {/* Venue Badge */}
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                    label={conference}
                                    size="small"
                                    color={isTopVenue ? "primary" : "default"}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        height: 24,
                                        borderRadius: "6px"
                                    }}
                                />
                            </Box>

                            {/* Paper Title */}
                            <Typography
                                variant="subtitle1"
                                component="div"
                                fontWeight={700}
                                lineHeight={1.35}
                                sx={{
                                    cursor: "pointer",
                                    "&:hover": { color: "primary.main" }
                                }}
                                onClick={() => window.open(projectPage || arxivLink, "_blank")}
                            >
                                {title}
                            </Typography>

                            {/* Authors */}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.8, lineHeight: 1.45 }}
                            >
                                {authors.map((author, index) => {
                                    const isTargetAuthor = author.includes(boldName);
                                    return (
                                        <span
                                            key={index}
                                            style={{
                                                fontWeight: isTargetAuthor ? 700 : 400,
                                                color: isTargetAuthor ? "inherit" : undefined
                                            }}
                                        >
                                            {author}
                                            {index < authors.length - 1 && ", "}
                                        </span>
                                    );
                                })}
                            </Typography>

                            {/* Action Links */}
                            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mt={1.8}>
                                {projectPage && projectPage !== arxivLink && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        href={projectPage}
                                        target="_blank"
                                        rel="noopener"
                                        endIcon={<LaunchIcon sx={{ fontSize: 13 }} />}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "6px",
                                            fontSize: "0.75rem",
                                            py: 0.3,
                                            px: 1.2
                                        }}
                                    >
                                        Project Page
                                    </Button>
                                )}

                                {arxivLink && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        href={arxivLink}
                                        target="_blank"
                                        rel="noopener"
                                        endIcon={<LaunchIcon sx={{ fontSize: 13 }} />}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: "6px",
                                            fontSize: "0.75rem",
                                            py: 0.3,
                                            px: 1.2
                                        }}
                                    >
                                        arXiv
                                    </Button>
                                )}

                                {bibtex && (
                                    <>
                                        <Tooltip title={copied ? "Copied to Clipboard!" : "Copy BibTeX"}>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={handleCopyBibtex}
                                                startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
                                                sx={{
                                                    textTransform: "none",
                                                    fontSize: "0.75rem",
                                                    py: 0.3,
                                                    px: 1
                                                }}
                                            >
                                                {copied ? "Copied!" : "BibTeX"}
                                            </Button>
                                        </Tooltip>

                                        <IconButton
                                            size="small"
                                            onClick={() => setBibtexOpen(true)}
                                            title="View BibTeX"
                                            sx={{ p: 0.5 }}
                                        >
                                            <CodeIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Grid>
                </Grid>
            </Card>

            {/* BibTeX Modal */}
            {bibtex && (
                <Dialog
                    open={bibtexOpen}
                    onClose={() => setBibtexOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: { borderRadius: "12px" } }}
                >
                    <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle1" fontWeight={700}>BibTeX Citation</Typography>
                        <IconButton size="small" onClick={() => setBibtexOpen(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box
                            component="pre"
                            sx={{
                                p: 1.5,
                                background: (theme) => theme.palette.mode === "dark" ? "#0f172a" : "#f1f5f9",
                                borderRadius: "8px",
                                fontSize: "0.8rem",
                                overflowX: "auto",
                                fontFamily: "monospace",
                                m: 0
                            }}
                        >
                            {bibtex}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 1.5 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            onClick={handleCopyBibtex}
                            sx={{ textTransform: "none", borderRadius: "6px" }}
                        >
                            {copied ? "Copied!" : "Copy to Clipboard"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

export default PublicationCard;
